import { IRuntime } from '@refly/common-types';

declare global {
  interface Window {
    ENV?: {
      API_URL?: string;
      COLLAB_URL?: string;
      STATIC_PUBLIC_ENDPOINT?: string;
      STATIC_PRIVATE_ENDPOINT?: string;
      SUBSCRIPTION_ENABLED?: boolean;
      CANVAS_TEMPLATE_ENABLED?: boolean;
      SENTRY_ENABLED?: boolean;
      ENV_TAG?: string;
    };

    ipcRenderer?: {
      invoke: (channel: string, args: any) => Promise<any>;
    };

    electronEnv?: {
      getApiBaseUrl: () => string;
      getCollabUrl: () => string;
      getPublicStaticEndpoint: () => string;
      getPrivateStaticEndpoint: () => string;
    };
  }
}

/**
 * Checks if code is running in browser environment and safely accesses window properties
 */
const isBrowser = typeof window !== 'undefined';

/**
 * Safely access window properties in browser environment
 */
const getBrowserValue = <T>(getter: () => T, fallback: T): T => {
  if (!isBrowser) return fallback;
  try {
    return getter() ?? fallback;
  } catch {
    return fallback;
  }
};

export const serverOrigin = (() => {
  const electronValue = getBrowserValue(() => window.electronEnv?.getApiBaseUrl?.(), '');
  if (electronValue) return electronValue;

  const envApiUrl =
    getBrowserValue(() => window.ENV?.API_URL, '') || process.env.VITE_API_URL || '';
  if (envApiUrl) {
    return envApiUrl;
  }
  return '';
})();
console.log('serverOrigin', serverOrigin);

export const wsServerOrigin = (() => {
  const electronValue = getBrowserValue(() => window.electronEnv?.getCollabUrl?.(), '');
  if (electronValue) return electronValue;

  const envValue =
    getBrowserValue(() => window.ENV?.COLLAB_URL, '') || process.env.VITE_COLLAB_URL || '';
  if (envValue) {
    return envValue;
  }
  if (isBrowser) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws`;
  }
  return '/ws';
})();
console.log('wsServerOrigin', wsServerOrigin);

export const staticPublicEndpoint = (() => {
  const electronValue = getBrowserValue(() => window.electronEnv?.getPublicStaticEndpoint?.(), '');
  if (electronValue) return electronValue;

  const envValue =
    getBrowserValue(() => window.ENV?.STATIC_PUBLIC_ENDPOINT, '') ||
    process.env.VITE_STATIC_PUBLIC_ENDPOINT ||
    '';
  if (envValue) {
    return envValue;
  }
  return '/v1/misc/public';
})();

export const staticPrivateEndpoint = (() => {
  const electronValue = getBrowserValue(() => window.electronEnv?.getPrivateStaticEndpoint?.(), '');
  if (electronValue) return electronValue;

  const envValue =
    getBrowserValue(() => window.ENV?.STATIC_PRIVATE_ENDPOINT, '') ||
    process.env.VITE_STATIC_PRIVATE_ENDPOINT ||
    '';
  if (envValue) {
    return envValue;
  }
  return '/v1/misc';
})();

export const subscriptionEnabled =
  getBrowserValue(() => Boolean(window.ENV?.SUBSCRIPTION_ENABLED), false) ||
  Boolean(process.env.VITE_SUBSCRIPTION_ENABLED);

export const canvasTemplateEnabled =
  getBrowserValue(() => Boolean(window.ENV?.CANVAS_TEMPLATE_ENABLED), false) ||
  Boolean(process.env.VITE_CANVAS_TEMPLATE_ENABLED);

export const sentryEnabled =
  getBrowserValue(() => Boolean(window.ENV?.SENTRY_ENABLED), false) ||
  Boolean(process.env.VITE_SENTRY_ENABLED);

export const envTag = getBrowserValue(() => window.ENV?.ENV_TAG, '') || process.env.VITE_ENV_TAG;
console.log('envTag', envTag);

export const runtime = process.env.VITE_RUNTIME as IRuntime;

export const isDesktop = () => runtime === 'desktop';
